/**
 * Spuren — was ein Mensch an Diagrammen aufbaut.
 *
 * DAS IST BEDIENZUSTAND UND KEINE RECHENGRÖSSE. Welche Kurven in welcher Spur
 * liegen, in welcher Reihenfolge die Spuren stehen und welche gerade
 * ausgeblendet ist, ändert keine einzige Zahl. Es gehört deshalb NICHT in
 * `SimState` — dort steht, was gerechnet wird, und sobald sich beides mischt,
 * ist nicht mehr erkennbar, was eine Zahl beeinflusst.
 *
 * Die Verwaltung steht trotzdem hier und nicht in der Seite, weil sie eine
 * Rechnung ist: was beim Überlagern zweier Spuren, beim Teilen einer Spur oder
 * beim Wegfallen eines Kanals herauskommt, hat eine Regel — und Regeln gehören
 * geprüft.
 *
 * EINE SPUR DARF EINHEITEN MISCHEN. Das ist die Entscheidung der Vorlage und
 * sie ist die richtige: wer wissen will, ob das Kniemoment vor oder nach der
 * Bodenkraft seine Spitze hat, muss beide übereinanderlegen. Newtonmeter und
 * Newton auf EINER Achse ergäben ein Bild, in dem die kleinere Grösse flach am
 * Boden liegt — deshalb wird in einer gemischten Spur jeder Kanal auf seinen
 * eigenen Bereich normiert und die Achse sagt genau das. Die Form der Kurven
 * ist dann vergleichbar, die Zahlenwerte sind es nicht, und niemand kann es
 * verwechseln.
 */

export interface Trace {
  /** Stabil über jedes Umsortieren — daran hängt der React-Schlüssel. */
  id: string
  /** Die Kanäle in dieser Spur, in der Reihenfolge, in der sie kamen. */
  keys: string[]
  /**
   * Sichtbar.
   *
   * Nicht dasselbe wie entfernt: die Auswahl bleibt stehen, die Spur wird nur
   * stumm. Wer sechs Spuren aufgebaut hat und eine davon gerade nicht braucht,
   * soll sie nicht abräumen und neu zusammensuchen müssen.
   */
  visible: boolean
}

/**
 * Womit die Seite aufmacht.
 *
 * NICHT WILLKÜRLICH: es ist der Satz, den ein Gangbild braucht, um beurteilt zu
 * werden — die drei sagittalen Winkel im Seitenvergleich, die drei Momente der
 * Standbeinkette übereinander, die Gelenkleistungen daneben und die senkrechte
 * Bodenkraft beider Seiten. Wer die Seite öffnet, sieht keine leere Fläche mit
 * der Aufforderung, sich etwas auszusuchen.
 */
export const DEFAULT_TRACES: readonly (readonly string[])[] = [
  ['A|hip|sag|R', 'A|hip|sag|L'],
  ['A|knee|sag|R', 'A|knee|sag|L'],
  ['A|ankle|sag|R', 'A|ankle|sag|L'],
  ['M|hip|sag|R', 'M|knee|sag|R', 'M|ankle|sag|R'],
  ['P|hip|R', 'P|knee|R', 'P|ankle|R'],
  ['G|v|R', 'G|v|L'],
]

/**
 * Fertige Sätze — ein Klick baut den Stapel für eine Frage.
 *
 * WOFÜR: wer die Rückgewinnung beurteilen will, braucht Gelenkleistung und
 * Akkuleistung derselben Achse übereinander, dazu die Bilanz. Das sind sechs
 * Kanäle in drei Spuren, und sie einzeln zusammenzusuchen ist jedes Mal
 * dieselbe Arbeit. Der Satz ist keine Voreinstellung, sondern eine Abkürzung:
 * danach lässt sich alles daran verschieben, überlagern und wegnehmen.
 */
export const TRACE_SETS: { id: string; label: string; traces: readonly (readonly string[])[] }[] = [
  {
    id: 'kinSag',
    label: 'Kinematik sagittal',
    traces: DEFAULT_TRACES.slice(0, 3),
  },
  {
    id: 'kinetR',
    label: 'Kinetik rechts',
    traces: [
      ['M|hip|sag|R', 'M|knee|sag|R', 'M|ankle|sag|R'],
      ['P|hip|R', 'P|knee|R', 'P|ankle|R'],
      ['G|v|R'],
    ],
  },
  {
    id: 'energie',
    label: 'Energie',
    traces: [['E|hip|R', 'E|knee|R', 'E|ankle|R'], ['NP|net']],
  },
  {
    id: 'recup',
    label: 'Rückgewinnung',
    traces: [
      ['P|ankle|R', 'NP|ankle|R'],
      ['P|knee|R', 'NP|knee|R'],
      ['NP|net', 'NP|hold'],
    ],
  },
  {
    id: 'dyn',
    label: 'Beschleunigung',
    traces: [['C|hip|sag|R'], ['C|knee|sag|R'], ['C|ankle|sag|R']],
  },
  {
    id: 'grf',
    label: 'Bodenreaktion',
    traces: [
      ['G|v|R', 'G|v|L'],
      ['G|ap|R', 'G|ap|L'],
      ['D|com|y'],
    ],
  },
]

/** Höhe einer Spur in Pixeln — dieselbe Grösse wie in der Vorlage. */
export const TRACE_HEIGHT = 84

let counter = 0

/**
 * Eine neue Kennung.
 *
 * Ein Zähler und kein Zufall: der Rechenkern verbietet sich Zufall, und auch
 * wenn dies Bedienzustand ist, macht eine wiederholbare Kennung den Test
 * lesbar. Sie wird nie gespeichert und muss nur innerhalb einer Sitzung
 * eindeutig sein.
 */
export const traceId = (): string => `spur-${++counter}`

/** Nur für Tests: den Zähler zurücksetzen. */
export const resetTraceIds = (): void => {
  counter = 0
}

export const newTrace = (keys: readonly string[]): Trace => ({
  id: traceId(),
  keys: [...keys],
  visible: true,
})

/** Ein Satz Spuren, gefiltert auf das, was diese Bewegung wirklich hergibt. */
export const tracesFrom = (
  sets: readonly (readonly string[])[],
  exists: (key: string) => boolean,
): Trace[] =>
  sets
    .map((keys) => keys.filter(exists))
    .filter((keys) => keys.length > 0)
    .map(newTrace)

/** Der Startzustand. */
export const defaultTraces = (exists: (key: string) => boolean): Trace[] =>
  tracesFrom(DEFAULT_TRACES, exists)

/**
 * Legt einen Kanal als EIGENE Spur ans Ende.
 *
 * Nicht in eine vorhandene Spur passender Einheit: was zusammengehört,
 * entscheidet die Frage und nicht die Einheit. Zwei Spuren übereinanderzulegen
 * ist ein Zug mit der Maus; sie wieder auseinanderzunehmen, wenn das System sie
 * ungefragt zusammengelegt hat, ist Sucharbeit.
 */
export function addChannel(traces: Trace[], key: string): Trace[] {
  if (hasChannel(traces, key)) return traces
  return [...traces, newTrace([key])]
}

/**
 * Nimmt einen Kanal aus ALLEN Spuren.
 *
 * Eine Spur, die dabei leer wird, verschwindet. Ein leeres Diagramm ist eine
 * Achse ohne Inhalt und beantwortet nichts.
 */
export function removeChannel(traces: Trace[], key: string): Trace[] {
  if (!hasChannel(traces, key)) return traces
  return traces
    .map((t) => ({ ...t, keys: t.keys.filter((k) => k !== key) }))
    .filter((t) => t.keys.length > 0)
}

/** Ist dieser Kanal irgendwo im Stapel? */
export const hasChannel = (traces: Trace[], key: string): boolean =>
  traces.some((t) => t.keys.includes(key))

/** Alle Kanäle, in der Reihenfolge der Spuren. */
export const channelsOf = (traces: Trace[]): string[] => traces.flatMap((t) => t.keys)

/**
 * Teilt eine Spur auf: der erste Kanal bleibt, jeder weitere wird eine eigene
 * Spur direkt darunter.
 *
 * Die Gegenbewegung zum Überlagern. Ohne sie ist ein versehentlich
 * zusammengezogenes Paar nur noch über Wegwerfen und Neusuchen zu trennen.
 */
export function splitTrace(traces: Trace[], id: string): Trace[] {
  const index = traces.findIndex((t) => t.id === id)
  if (index < 0 || traces[index].keys.length < 2) return traces
  const [first, ...rest] = traces[index].keys
  return [
    ...traces.slice(0, index),
    { ...traces[index], keys: [first] },
    ...rest.map((key) => newTrace([key])),
    ...traces.slice(index + 1),
  ]
}

/**
 * Legt eine Spur auf eine andere.
 *
 * Die gezogene Spur verschwindet, ihre Kanäle hängen sich an die Zielspur an —
 * doppelte werden nicht zweimal aufgenommen. Zwei gleiche Kurven in einem Bild
 * sind eine Linie und zwei Legendeneinträge.
 */
export function mergeTrace(traces: Trace[], fromId: string, intoId: string): Trace[] {
  if (fromId === intoId) return traces
  const from = traces.find((t) => t.id === fromId)
  const into = traces.find((t) => t.id === intoId)
  if (!from || !into) return traces
  const keys = [...into.keys, ...from.keys.filter((k) => !into.keys.includes(k))]
  return traces.filter((t) => t !== from).map((t) => (t === into ? { ...t, keys } : t))
}

/** Entfernt eine Spur samt ihrer Kanäle. */
export const closeTrace = (traces: Trace[], id: string): Trace[] =>
  traces.filter((t) => t.id !== id)

/** Blendet eine Spur aus oder wieder ein. */
export const toggleVisible = (traces: Trace[], id: string): Trace[] =>
  traces.map((t) => (t.id === id ? { ...t, visible: !t.visible } : t))

/**
 * Schiebt eine Spur an eine andere Stelle.
 *
 * Der Zielindex zählt in der Liste NACH dem Herausnehmen — so verhält sich das
 * Ziehen, wie man es erwartet: eine Spur an die dritte Stelle gezogen landet an
 * der dritten Stelle, egal ob sie vorher davor oder dahinter stand.
 */
export function moveTrace(traces: Trace[], from: number, to: number): Trace[] {
  if (from === to || from < 0 || from >= traces.length) return traces
  const out = [...traces]
  const [moved] = out.splice(from, 1)
  out.splice(Math.max(0, Math.min(out.length, to)), 0, moved)
  return out
}

/**
 * Wirft Kanäle weg, die es nicht mehr gibt.
 *
 * Wer die Bewegung wechselt, verliert die Achsen, die es dort nicht gibt. Eine
 * Spur, die dabei leer wird, verschwindet; die übrigen bleiben stehen. Die
 * Auswahl bei jedem Wechsel zu leeren wäre bequemer zu schreiben und lästiger
 * zu bedienen.
 */
export function pruneChannels(traces: Trace[], exists: (key: string) => boolean): Trace[] {
  const out = traces
    .map((t) => ({ ...t, keys: t.keys.filter(exists) }))
    .filter((t) => t.keys.length > 0)
  /* Unverändert? Dann dieselbe Referenz zurück — sonst rechnet die Seite bei
     jedem Durchlauf neu, obwohl sich nichts geändert hat. */
  const same =
    out.length === traces.length &&
    out.every((t, i) => t.id === traces[i].id && t.keys.length === traces[i].keys.length)
  return same ? traces : out
}

/**
 * In welcher Reihenfolge die acht Serienfarben vergeben werden.
 *
 * NICHT DER REIHE NACH. `--chart-2` ist im dunklen Register nahezu weiss und
 * `--chart-6` ein Grau — beide sind gute Farben für die dritte und vierte Kurve
 * und schlechte für die zweite: eine weisse Linie neben einer farbigen liest
 * sich als „die eine ist wichtig, die andere ist Kontext", und gemeint ist ein
 * Vergleich zweier gleichrangiger Kurven. Die bunten Töne kommen deshalb
 * zuerst, die unbunten zuletzt.
 */
const TONE_ORDER = [1, 3, 5, 4, 7, 8, 2, 6]

/** Wie viele Serienfarben es gibt — `--chart-1` bis `--chart-8`. */
export const TONES = TONE_ORDER.length

/**
 * Welcher Kanal welchen Farbton bekommt.
 *
 * DIE FARBE HÄNGT AM KANAL UND NICHT AN SEINEM PLATZ IN DER SPUR. Wer eine Spur
 * auf eine andere legt, soll seine Kurve an der Farbe wiederfinden; würde der
 * Ton aus dem Index in der Spur folgen, wechselte beim Überlagern jede zweite
 * Kurve die Farbe.
 *
 * Vergeben wird der niedrigste Ton, den der GANZE STAPEL noch nicht führt — und
 * nicht der, den die eigene Spur noch nicht führt. Der Unterschied ist keiner
 * der Ordnungsliebe: liesse man jede Spur für sich zählen, bekämen Hüfte, Knie
 * und Sprunggelenk in ihren drei Winkelspuren alle denselben Ton, und beim
 * Zusammenlegen der drei Momente in EINE Spur stünden drei gleichfarbige Kurven
 * übereinander. Ein Ton, der einmal vergeben ist, gehört seinem Kanal.
 *
 * Ab der neunten Kurve wiederholen sich die Töne. Das ist kein Fehler, sondern
 * der Punkt, an dem Farbe ohnehin nicht mehr unterscheidet.
 */
export function channelTones(traces: Trace[]): Map<string, number> {
  const out = new Map<string, number>()
  const used = new Set<number>()
  for (const trace of traces) {
    /* Was diese Spur schon führt — für den Fall, dass die Palette aufgebraucht
       ist. Dann zählt nur noch, dass zwei Kurven IN EINEM BILD verschieden
       sind; über den Stapel hinweg ist die Farbe ab da ohnehin kein Merkmal
       mehr. */
    const here = new Set<number>()
    for (const key of trace.keys) {
      const tone = out.get(key)
      if (tone !== undefined) here.add(tone)
    }
    for (const key of trace.keys) {
      if (out.has(key)) continue
      const tone =
        TONE_ORDER.find((t) => !used.has(t)) ??
        TONE_ORDER.find((t) => !here.has(t)) ??
        TONE_ORDER[out.size % TONES]
      used.add(tone)
      here.add(tone)
      out.set(key, tone)
    }
  }
  return out
}
